import { useCallback, useRef } from 'react'

export interface UseDropdownInfiniteScrollOptions {
  /**
   * The current page from your query hook
   */
  currentPage?: number
  /**
   * Whether the dropdown query is loading
   */
  isLoading: boolean
  /**
   * Whether the next page is being fetched (from useInfiniteQuery)
   */
  isFetchingNextPage?: boolean
  /**
   * Whether all pages have been fetched
   */
  isComplete: boolean
  /**
   * Callback to trigger the next page fetch
   */
  fetchNextPage: () => Promise<unknown> | void
  /**
   * The current search string associated with this dropdown
   */
  currentSearch: string
  /**
   * Setter for the current search string
   */
  setSearch: (value: string) => void
}

export interface DropdownInfiniteScrollProps {
  fetch: (filterInput?: string, requestedPage?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

/**
 * Provides consistent infinite scroll logic for dropdown components
 */
export function useDropdownInfiniteScroll({
  currentPage = 0,
  isLoading,
  isFetchingNextPage,
  isComplete,
  fetchNextPage,
  currentSearch,
  setSearch,
}: UseDropdownInfiniteScrollOptions): DropdownInfiniteScrollProps {
  const fetchingRef = useRef(false)
  const lastFetchedPageRef = useRef(0)

  const fetch = useCallback(
    (filterInput?: string, requestedPage?: number) => {
      const q = (filterInput ?? '').trim()
      const target = requestedPage ?? 0

      // initial fetch / filter reset
      if (target === 0) {
        if (q !== currentSearch) setSearch(q)
        return
      }

      if (
        q === currentSearch &&
        target > currentPage &&
        target > lastFetchedPageRef.current &&
        !isLoading &&
        !isFetchingNextPage &&
        !isComplete &&
        !fetchingRef.current
      ) {
        fetchingRef.current = true
        lastFetchedPageRef.current = target
        Promise.resolve(fetchNextPage())
          .catch(() => {
            lastFetchedPageRef.current = currentPage
          })
          .finally(() => {
            fetchingRef.current = false
          })
      }
    },
    [
      currentPage,
      currentSearch,
      isLoading,
      isFetchingNextPage,
      isComplete,
      fetchNextPage,
      setSearch,
    ],
  )

  return {
    fetch,
    page: currentPage,
    loading: isFetchingNextPage || isLoading,
    complete: isComplete,
  }
}
